import {
  type ComponentTree,
  createFigmaClipboardParser,
  type TransformOptions,
  transformClipboard,
} from '@moatless/figma'
import { useCallback, useState } from 'react'
import rfdc from 'rfdc'

const clone = rfdc()

export interface ParsedResult {
  componentTree: ComponentTree
  rawClipboardJson: unknown
  fileName: string
  nodeName: string
  page1Canvas: unknown | null
  internalCanvas: unknown | null
}

export interface UseFigmaTestParserReturn {
  parse: (input: string, options: TransformOptions) => void
  result: ParsedResult | null
  error: string | null
  isLoading: boolean
  clear: () => void
}

function extractCanvasChildren(clipboardData: unknown): {
  page1Canvas: unknown | null
  internalCanvas: unknown | null
} {
  if (
    clipboardData &&
    typeof clipboardData === 'object' &&
    'root' in clipboardData &&
    clipboardData.root &&
    typeof clipboardData.root === 'object' &&
    'children' in clipboardData.root &&
    Array.isArray(clipboardData.root.children)
  ) {
    const children = clipboardData.root.children
    return {
      page1Canvas: children.length > 0 ? clone(children[0]) : null,
      internalCanvas: children.length > 1 ? clone(children[1]) : null,
    }
  }

  return { page1Canvas: null, internalCanvas: null }
}

export function useFigmaTestParser(): UseFigmaTestParserReturn {
  const [result, setResult] = useState<ParsedResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const parse = useCallback((input: string, options: TransformOptions) => {
    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      let clipboardData: unknown

      const isHtmlClipboard = input.includes('data-metadata') && input.includes('data-buffer')

      if (isHtmlClipboard) {
        // Parse HTML clipboard client-side using the figma package parser.
        // Buffer is polyfilled in main.tsx so this works in the browser.
        try {
          const parser = createFigmaClipboardParser()
          clipboardData = parser.parseClipboardHTML(input)
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to parse HTML clipboard'
          throw new Error(`HTML clipboard parsing failed: ${message}`)
        }
      } else {
        try {
          clipboardData = JSON.parse(input)
        } catch {
          throw new Error('Invalid input: Not valid JSON or Figma clipboard HTML format')
        }
      }

      const { page1Canvas, internalCanvas } = extractCanvasChildren(clipboardData)

      const componentTree = transformClipboard(clipboardData, options)

      const fileName = componentTree.source?.fileName ?? 'Figma Design'
      const nodeName = componentTree.root.name ?? 'Component'

      setResult({
        componentTree,
        rawClipboardJson: clipboardData,
        fileName,
        nodeName,
        page1Canvas,
        internalCanvas,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const clear = useCallback(() => {
    setResult(null)
    setError(null)
    setIsLoading(false)
  }, [])

  return {
    parse,
    result,
    error,
    isLoading,
    clear,
  }
}
